import { useMemo } from 'react'
import { Table, TableBody, TableHead, TableHeader, TableRow, cn } from '@ludus/ui'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Manager } from '@ludus/core'
import { mockManagers } from '@/shared/mocks/managers.ts'
import { funnelChannelFor } from '@/shared/utils/funnels.ts'
import type { PipelineApiError } from '@/lib/api/pipelines.ts'
import type { LeadView, SortKey } from '../../types.ts'
import { useStageState } from '../useStageState.ts'
import { LeadRow } from './LeadRow.tsx'
import { EmptyLeads, LeadsError, LeadsLoading } from './empty.tsx'

interface LeadsTableProps {
  /** The current server-paginated page of leads (already filtered + sorted). */
  leads: LeadView[]
  /** Full filtered count across all pages — drives the "X–Y of N" summary + nav. */
  total: number
  managers: Manager[]
  now: Date
  /** First-load with no rows yet — show a loading state, not the empty state. */
  isLoading?: boolean
  /** A failed read — show an error state, never a silent empty board. */
  error?: PipelineApiError | null
}

// Stagger cap — past this row index every row shares the max delay, so the
// cascade stays ~snappy regardless of page size (10 · 28ms = 280ms + 200ms anim).
const RISE_STEP_MS = 28
const RISE_MAX_STEPS = 10

export function LeadsTable({ leads, total, managers, now, isLoading, error }: LeadsTableProps) {
  const { t } = useTranslation()
  const sort = useStageState((s) => s.sort)
  const toggleSort = useStageState((s) => s.toggleSort)

  const managerById = useMemo(() => new Map(managers.map((m) => [m.id, m])), [managers])
  const mockById = useMemo(() => new Map(mockManagers.map((m) => [m.id, m])), [])

  // Replay the entrance cascade only when the actually-rendered rows change —
  // keyed on the loaded data, not on UI state. With SWR `keepPreviousData` the
  // previous page stays put (no animation) until the new data lands, then the
  // cascade fires once on the fresh rows. Keying on UI state instead made it
  // fire immediately on the stale (kept) rows and then again on the swap.
  const riseKey = leads.map((l) => l.id).join(',')

  // A failed read and a first-load must be visually distinct from "no leads".
  if (error) return <LeadsError error={error} />
  if (isLoading) return <LeadsLoading />
  if (total === 0) return <EmptyLeads />

  return (
    <div className="flex flex-col">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('pages.pipelines.columns.deal', { defaultValue: 'Deal' })}</TableHead>
            <TableHead>{t('pages.pipelines.columns.owner', { defaultValue: 'Owner' })}</TableHead>
            <TableHead>{t('pages.pipelines.columns.funnel', { defaultValue: 'Funnel' })}</TableHead>
            <SortableHead
              activeKey={sort.key}
              direction={sort.direction}
              sortKey="value"
              onClick={() => toggleSort('value')}
              label={t('pages.pipelines.columns.value', { defaultValue: 'Value' })}
            />
            <SortableHead
              activeKey={sort.key}
              direction={sort.direction}
              sortKey="age"
              onClick={() => toggleSort('age')}
              label={t('pages.pipelines.columns.age', { defaultValue: 'Age' })}
            />
            <TableHead>{t('pages.pipelines.columns.tags', { defaultValue: 'Tags' })}</TableHead>
            <SortableHead
              activeKey={sort.key}
              direction={sort.direction}
              sortKey="lastAction"
              onClick={() => toggleSort('lastAction')}
              label={t('pages.pipelines.columns.nextAction', { defaultValue: 'Next action' })}
            />
          </TableRow>
        </TableHeader>
        <TableBody key={riseKey}>
          {leads.map((lead, index) => {
            const manager = lead.assignedManagerId
              ? (managerById.get(lead.assignedManagerId) ?? null)
              : null
            const mockManager = lead.assignedManagerId
              ? mockById.get(lead.assignedManagerId)
              : undefined
            return (
              <LeadRow
                key={lead.id}
                lead={lead}
                manager={manager}
                funnel={lead.funnelKey ? funnelChannelFor(lead.funnelKey) : null}
                managerColor={mockManager?.color ?? null}
                managerInitials={mockManager?.initials ?? null}
                now={now}
                riseDelayMs={Math.min(index, RISE_MAX_STEPS) * RISE_STEP_MS}
              />
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

function SortableHead({
  activeKey,
  direction,
  sortKey,
  onClick,
  label,
}: {
  activeKey: SortKey
  direction: 'asc' | 'desc'
  sortKey: SortKey
  onClick: () => void
  label: string
}) {
  const isActive = activeKey === sortKey
  return (
    <TableHead>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 transition-colors hover:text-foreground',
          isActive && 'text-foreground',
        )}
      >
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden="true" />
          )
        ) : null}
      </button>
    </TableHead>
  )
}
